import React, { useState } from 'react';

/**
 * Expandable panel showing the statutory deadline calculation trace.
 * Provides lawyer-facing transparency into how a deadline date was computed.
 */

interface DeadlineTrace {
  /** From statutory_deadline.triggered_by (optional — not in engine result) */
  trigger_event?: string;
  /** From legal_context.trigger_date (optional — not in engine result) */
  trigger_date?: string;
  /** From statutory_deadline.calculation (optional — not in engine result) */
  calculation_type?: 'calendar_days' | 'business_days' | 'court_days' | string;
  /** From statutory_deadline.due_date_offset_days (optional — not in engine result) */
  base_days?: number;
  /** Engine result adjustments — backend uses adjusted_date, frontend uses resulting_date */
  adjustments: Array<{
    reason: string;
    days_added: number;
    resulting_date?: string;
    adjusted_date?: string;
  }>;
  final_date: string;
  statute_citation: string;
}

interface DeadlineTracePanelProps {
  trace: DeadlineTrace;
  statuteReference?: string | null;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

const calculationTypeLabels: Record<string, string> = {
  calendar_days: 'Calendar Days',
  business_days: 'Business Days',
  court_days: 'Court Days',
};

const DeadlineTracePanel: React.FC<DeadlineTracePanelProps> = ({ trace, statuteReference }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border border-amber-200 dark:border-amber-800 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-3 py-2 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          {/* Clock icon */}
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-amber-600 dark:text-amber-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.828a1 1 0 101.415-1.414L11 9.586V6z" clipRule="evenodd" />
          </svg>
          <span className="text-sm font-medium text-amber-800 dark:text-amber-300">
            Deadline Trace
          </span>
          {trace.statute_citation && (
            <span className="text-xs text-amber-600 dark:text-amber-400">
              ({trace.statute_citation})
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-amber-900 dark:text-amber-200">
            {formatDate(trace.final_date)}
          </span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`h-4 w-4 text-amber-600 dark:text-amber-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </div>
      </button>

      {expanded && (
        <div className="px-3 py-3 bg-white dark:bg-gray-800 space-y-3 text-sm">
          {/* Summary row */}
          <div className="grid grid-cols-2 gap-3 text-xs">
            {trace.trigger_event && (
              <div>
                <span className="text-gray-500 dark:text-gray-400">Trigger: </span>
                <span className="text-gray-900 dark:text-white font-medium">
                  {trace.trigger_event.replace(/_/g, ' ')}
                </span>
              </div>
            )}
            {trace.trigger_date && (
              <div>
                <span className="text-gray-500 dark:text-gray-400">Trigger Date: </span>
                <span className="text-gray-900 dark:text-white font-medium">
                  {formatDate(trace.trigger_date)}
                </span>
              </div>
            )}
            {trace.base_days != null && trace.calculation_type && (
              <div>
                <span className="text-gray-500 dark:text-gray-400">Calculation: </span>
                <span className="text-gray-900 dark:text-white font-medium">
                  +{trace.base_days} {calculationTypeLabels[trace.calculation_type] || trace.calculation_type}
                </span>
              </div>
            )}
            {statuteReference && (
              <div>
                <span className="text-gray-500 dark:text-gray-400">Statute: </span>
                <span className="text-gray-900 dark:text-white font-medium">
                  {statuteReference}
                </span>
              </div>
            )}
          </div>

          {/* Adjustment steps */}
          {trace.adjustments.length > 0 && (
            <div className="space-y-1.5">
              <h5 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Adjustments
              </h5>
              {trace.adjustments.map((adj, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between text-xs p-2 bg-gray-50 dark:bg-gray-700/50 rounded"
                >
                  <span className="text-gray-700 dark:text-gray-300">{adj.reason}</span>
                  <div className="flex items-center gap-2">
                    <span className={adj.days_added > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-green-600 dark:text-green-400'}>
                      {adj.days_added > 0 ? '+' : ''}{adj.days_added} day{Math.abs(adj.days_added) !== 1 ? 's' : ''}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400">
                      {formatDate(adj.resulting_date || adj.adjusted_date || '')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Final result */}
          <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
            <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
              Final Deadline
            </span>
            <span className="text-sm font-bold text-red-600 dark:text-red-400">
              {formatDate(trace.final_date)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeadlineTracePanel;
